import { useState, useEffect } from "react";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { TESTIMONIALS } from "@/lib/site";
import Reveal from "@/components/Reveal";

export default function Testimonials() {
  const [perView, setPerView] = useState(1);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const calc = () => setPerView(1);
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  const maxIndex = Math.max(0, TESTIMONIALS.length - perView);

  useEffect(() => {
    if (index > maxIndex) setIndex(maxIndex);
  }, [maxIndex, index]);

  const next = () => setIndex((i) => Math.min(i + 1, maxIndex));
  const prev = () => setIndex((i) => Math.max(i - 1, 0));

  const slideWidth = 100 / perView;
  const translate = -(index * slideWidth);

  return (
    <section
      data-testid="testimonials-section"
      className="relative bg-[#0E1213] py-24 md:py-32 lg:py-40 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <Reveal>
          <div className="flex items-center justify-between gap-6 mb-12 md:mb-16">
            <div className="flex items-center gap-4">
              <span className="block w-10 h-px bg-[#3FB8DC]" />
              <span className="eyebrow">Words from our clients</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid="testimonial-prev"
                onClick={prev}
                disabled={index === 0}
                aria-label="Previous"
                className="w-12 h-12 border border-[#27272A] hover:border-[#3FB8DC] hover:text-[#3FB8DC] flex items-center justify-center text-[#F4F0EB] transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-[#27272A] disabled:hover:text-[#F4F0EB]"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <button
                data-testid="testimonial-next"
                onClick={next}
                disabled={index >= maxIndex}
                aria-label="Next"
                className="w-12 h-12 border border-[#27272A] hover:border-[#3FB8DC] hover:text-[#3FB8DC] flex items-center justify-center text-[#F4F0EB] transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-[#27272A] disabled:hover:text-[#F4F0EB]"
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="overflow-hidden -mx-3">
            <div
              data-testid="testimonial-track"
              className="flex transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
              style={{ transform: `translateX(${translate}%)` }}
            >
              {TESTIMONIALS.map((t, i) => (
                <div
                  key={t.author}
                  className="flex-shrink-0 px-3"
                  style={{ width: `${slideWidth}%` }}
                >
                  <div
                    data-testid={`testimonial-${i}`}
                    className="bg-[#080A0B] border border-[#27272A] p-10 md:p-14 lg:p-20 h-full flex flex-col items-center text-center min-h-[340px]"
                  >
                    <Quote className="w-10 h-10 text-[#3FB8DC] mb-8 opacity-80" />
                    <p className="font-serif-display italic text-2xl md:text-3xl lg:text-4xl text-[#F4F0EB] leading-snug tracking-tight max-w-3xl">
                      "{t.quote}"
                    </p>
                    <div className="mt-10 pt-6 border-t border-[#27272A] w-32 text-center">
                      <p className="font-serif-display text-xl text-[#F4F0EB]">
                        {t.author}
                      </p>
                      <p className="eyebrow mt-2" style={{ fontSize: "0.55rem" }}>
                        {t.role}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="mt-10 flex justify-center gap-2">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Slide ${i + 1}`}
              data-testid={`testimonial-dot-${i}`}
              className={`h-[2px] transition-all ${
                i === index ? "w-10 bg-[#3FB8DC]" : "w-6 bg-[#27272A]"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
